import { useCallback, useEffect, useRef, useState } from "react";

const SCRIPT_SRC = "https://accounts.google.com/gsi/client";
let scriptPromise;
let activeOAuthPopup = null;
let activeOAuthPromise = null;

const loadGoogleScript = () => {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error("Failed to load Google identity script."));
    document.head.appendChild(script);
  });

  return scriptPromise;
};

const buildNonce = () => {
  if (window.crypto?.getRandomValues) {
    const array = new Uint32Array(4);
    window.crypto.getRandomValues(array);
    return Array.from(array, (n) => n.toString(16).padStart(8, "0")).join("");
  }
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
};

const requestGoogleCredential = async (clientId) => {
  const google = await loadGoogleScript();
  if (!google?.accounts?.id) {
    throw new Error("Google sign-in is unavailable right now.");
  }

  const attemptPrompt = (useFedCm, cancelOnTapOutside) =>
    new Promise((resolve, reject) => {
      let settled = false;
      let timeoutId;
      const finish = (callback) => {
        if (settled) return;
        settled = true;
        if (timeoutId) window.clearTimeout(timeoutId);
        google.accounts.id.cancel?.();
        callback();
      };

      google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => {
          if (response?.credential) {
            finish(() => resolve(response.credential));
          } else {
            finish(() => reject(new Error("No Google credential was returned.")));
          }
        },
        cancel_on_tap_outside: cancelOnTapOutside,
        use_fedcm_for_prompt: useFedCm,
        ux_mode: "popup",
      });

      google.accounts.id.prompt((notification) => {
        if (settled) return;

        const notDisplayedReason = notification.getNotDisplayedReason?.();
        const skippedReason = notification.getSkippedReason?.();
        const dismissedReason = notification.getDismissedReason?.();

        if (notDisplayedReason || skippedReason || dismissedReason) {
          const message =
            dismissedReason === "tap_outside"
              ? "Google sign-in was cancelled."
              : "Google sign-in was dismissed.";
          finish(() =>
            reject(
              new Error(
                `${message}${notDisplayedReason ? ` (${notDisplayedReason})` : ""}`
              )
            )
          );
        }
      });

      timeoutId = window.setTimeout(() => {
        finish(() => reject(new Error("Google sign-in timed out. Please try again.")));
      }, 12000);
    });

  try {
    return await attemptPrompt(true, true);
  } catch (err) {
    // Fallback without FedCM if the browser blocks the FedCM prompt (e.g., Safari/ITP).
    return attemptPrompt(false, false);
  }
};

const requestGoogleOAuthPopup = (clientId, intent = "login") => {
  if (activeOAuthPromise && activeOAuthPopup && !activeOAuthPopup.closed) {
    try {
      activeOAuthPopup.focus();
    } catch {
      /* ignore */
    }
    return activeOAuthPromise;
  }

  if (activeOAuthPromise && (!activeOAuthPopup || activeOAuthPopup.closed)) {
    activeOAuthPromise = null;
    activeOAuthPopup = null;
  }

  const state = buildNonce();
  const redirectUri = `${window.location.origin}/auth/google/callback`;
  sessionStorage.setItem("wayli.google.oauth.state", state);
  sessionStorage.setItem("wayli.google.oauth.intent", intent);

  const authUrl = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  authUrl.searchParams.set("client_id", clientId);
  authUrl.searchParams.set("redirect_uri", redirectUri);
  authUrl.searchParams.set("response_type", "id_token");
  authUrl.searchParams.set("scope", "openid email profile");
  authUrl.searchParams.set("state", state);
  authUrl.searchParams.set("nonce", state);
  authUrl.searchParams.set("prompt", "select_account");

  activeOAuthPromise = new Promise((resolve, reject) => {
    const popup = window.open(
      authUrl.toString(),
      "wayli_google_oauth",
      "width=500,height=620"
    );
    activeOAuthPopup = popup || null;

    if (!popup) {
      activeOAuthPromise = null;
      activeOAuthPopup = null;
      // Fall back to full-page redirect
      window.location.href = authUrl.toString();
      return;
    }

    const cleanup = () => {
      window.removeEventListener("message", handleMessage);
      if (pollTimer) window.clearInterval(pollTimer);
      sessionStorage.removeItem("wayli.google.oauth.state");
      activeOAuthPromise = null;
      activeOAuthPopup = null;
      try {
        if (!popup.closed) popup.close();
      } catch {
        /* ignore */
      }
    };

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type !== "wayli-google-oauth") return;
      if (data.state !== state) {
        cleanup();
        reject(new Error("Google sign-in state mismatch."));
        return;
      }
      if (data.error) {
        cleanup();
        reject(new Error("Google sign-in was cancelled."));
        return;
      }
      if (!data.idToken) {
        cleanup();
        reject(new Error("No Google token received."));
        return;
      }
      cleanup();
      resolve(data.idToken);
    };

    window.addEventListener("message", handleMessage);

    const pollTimer = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new Error("Google sign-in popup was closed."));
      }
    }, 500);
  });

  return activeOAuthPromise;
};

export default function useGoogleSignIn() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const mounted = useRef(true);
  const [isLoading, setIsLoading] = useState(false);
  const isReady = Boolean(clientId);
  const [error, setError] = useState("");

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const requestCredential = useCallback(async (options = {}) => {
    const intent = options.intent || "login";
    setError("");
    if (!clientId) {
      throw new Error("Google sign-in is not configured.");
    }

    setIsLoading(true);
    try {
      try {
        const idToken = await requestGoogleOAuthPopup(clientId, intent);
        return idToken;
      } catch (fallbackErr) {
        if (mounted.current) {
          setError(fallbackErr?.message ?? "Google sign-in failed.");
        }
        throw fallbackErr;
      }
    } finally {
      if (mounted.current) {
        setIsLoading(false);
      }
    }
  }, [clientId]);

  // Optional: GIS One Tap; only used if available and fast, otherwise we rely on popup/redirect
  const requestOneTapIfAvailable = useCallback(async () => {
    if (!clientId || !window.google?.accounts?.id) return null;
    try {
      const credential = await requestGoogleCredential(clientId);
      return credential;
    } finally {
      if (mounted.current) {
        setIsLoading(false);
      }
    }
  }, [clientId]);

  return { requestCredential, requestOneTapIfAvailable, isLoading, isReady, error };
}
